import { useHistory } from 'react-router'
import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { groupBy, map } from 'lodash'
import Loader from 'components/Loader'

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import Section from '../Section'
import { isLoadingCategories } from 'store/Categories/selectors'
import ModifierGroupItem from './ModifierGroupItem'
import {
  getIsItemsLoading,
  modifierGroupsWithAssignedItems,
} from 'store/Items/selector'
import { setCategoriesIndexes } from 'store/Categories/actions'
import { getCategoriesSortingMode } from 'store/AppState/selector'

const ModifierGroupList = ({ modifierGroups }) => {
  const isModifierGroupsLoding = useSelector(isLoadingCategories)
  const isItemsLoading = useSelector(getIsItemsLoading)
  const isLoading = isModifierGroupsLoding || isItemsLoading

  const [modifierGroupsList, setModifierGroupsList] =
    useState(modifierGroups)
  const categoriesSortingMode = useSelector(getCategoriesSortingMode)

  const assignedItemsToModifierGroup = useSelector(
    modifierGroupsWithAssignedItems
  )

  const history = useHistory()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(setCategoriesIndexes(modifierGroupsList))
  }, [modifierGroupsList, dispatch])

  useEffect(() => {
    if (!categoriesSortingMode) {
      setModifierGroupsList(modifierGroups)
    }
    // eslint-disable-next-line
  }, [modifierGroups.length, categoriesSortingMode])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const groupedAssignedItemsToModifierGroup = groupBy(
    assignedItemsToModifierGroup,
    'categoryUuid'
  )

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      setModifierGroupsList((modifierGroupsList) => {
        const oldIndex = modifierGroupsList.findIndex(
          (item) => item.uuid === active.id
        )
        const newIndex = modifierGroupsList.findIndex(
          (item) => item.uuid === over?.id
        )

        return arrayMove(modifierGroupsList, oldIndex, newIndex)
      })
    }
  }

  return isLoading ? (
    <Loader />
  ) : (
    <Section className="item-list">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={modifierGroupsList.map((item) => item.uuid)}
          strategy={verticalListSortingStrategy}
        >
          {map(modifierGroupsList, (modifierGroup) => (
            <ModifierGroupItem
              key={modifierGroup.uuid}
              uuid={modifierGroup.uuid}
              categoriesSortingMode={categoriesSortingMode}
              name={modifierGroup.canopyDisplayName}
              handleItemClick={() =>
                history.push(`modifier-groups/${modifierGroup.uuid}`)
              }
              assignedItems={
                groupedAssignedItemsToModifierGroup?.[
                  modifierGroup.uuid
                ]?.[0]?.itemsInCategory
              }
            />
          ))}
        </SortableContext>
      </DndContext>
    </Section>
  )
}

export default ModifierGroupList
